/**
 * "Add to bench" — the low-friction way to curate the assessment eval bench.
 *
 * The moment someone decides a case is worth benching is usually while they are
 * already reading its analysis, not while searching a picker.
 *
 * The target comes from the code-declared bench registry, so there is no
 * dropdown in the single-bench case — a bench is something the code knows
 * about, not something you pick from a list of datasets.
 * See docs/FeatureDesigns/Evals/assessment-eval-bench.md §4.2 (path B).
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { VariantQuality } from '../../types/scenarioEvals';

export function AddToBenchButton({ submissionId }: { submissionId: string }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button className="playground-link-button" onClick={() => setOpen(true)}
                title="Add this student solution to the assessment eval bench">
                Add to bench
            </button>
            {open && <AddToBenchDialog submissionId={submissionId} onClose={() => setOpen(false)} />}
        </>
    );
}

function AddToBenchDialog({ submissionId, onClose }: { submissionId: string; onClose: () => void }) {
    const [variantQuality, setVariantQuality] = useState<VariantQuality | ''>('');
    const [note, setNote] = useState('');
    const [chosen, setChosen] = useState('');

    const { data, isLoading } = useQuery({ queryKey: ['benches'], queryFn: () => adminApi.listBenches() });
    const benches = data?.benches ?? [];
    // One declared bench is the normal case — don't make them choose.
    const target = benches.length === 1 ? benches[0].datasetId : chosen;

    const add = useMutation({
        mutationFn: () => adminApi.captureScenarioInputs(target, {
            submissionIds: [submissionId],
            ...(variantQuality ? { variantQuality } : {}),
            ...(note ? { curationNote: note } : {}),
        }),
    });

    const done = add.isSuccess;

    return (
        <div className="bench-modal-backdrop" onClick={onClose}>
            <div className="bench-modal" onClick={e => e.stopPropagation()}>
                <h2>Add to bench</h2>

                {isLoading && <p className="bench-muted">Loading…</p>}

                {!done && !isLoading && (
                    <>
                        {benches.length === 1 && (
                            <p className="bench-muted">Adding to <strong>{benches[0].name}</strong>.</p>
                        )}
                        {benches.length > 1 && (
                            <label className="bench-note">
                                Bench
                                <select value={chosen} onChange={e => setChosen(e.target.value)}>
                                    <option value="">— pick —</option>
                                    {benches.map(b => <option key={b.datasetId} value={b.datasetId}>{b.name}</option>)}
                                </select>
                            </label>
                        )}

                        <label className="bench-note">
                            Variant
                            <select value={variantQuality} onChange={e => setVariantQuality(e.target.value as VariantQuality | '')}>
                                <option value="">— unset —</option>
                                <option value="correct">correct</option>
                                <option value="partially_correct">right answer, wrong method</option>
                                <option value="common_mistake">common mistake</option>
                            </select>
                        </label>

                        <label className="bench-note">
                            Why this row
                            <input type="text" value={note} onChange={e => setNote(e.target.value)}
                                placeholder="optional — what makes it worth benching" />
                        </label>
                    </>
                )}

                {done && (
                    <p>
                        {add.data?.captured
                            ? `Added ${add.data.captured} row.`
                            : 'Already on this bench — nothing added.'}
                    </p>
                )}
                {add.error && <p className="bench-error">{String(add.error)}</p>}

                <div className="bench-modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
                    {!done && (
                        <button className="btn btn-primary" disabled={!target || add.isPending}
                            onClick={() => add.mutate()}>
                            {add.isPending ? 'Adding…' : 'Add'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
